import { FC, Fragment, ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { JSONTree } from 'react-json-tree';
import XMLViewer from 'react-xml-viewer';
import {
  Box,
  BoxProps,
  FormControlLabel,
  Paper,
  paperClasses,
  Stack,
  styled,
  Switch,
  useTheme,
} from '@mui/material';
import classNames from 'classnames';

import { useOppositeColorMode } from '@hooks/useOppositeMode';

const Container = styled(Stack)`
  overflow-y: auto;

  &.wrap {
    overflow-x: auto;
  }
`;

const ControlWrapper = styled(Box)`
  position: sticky;
  top: 0;
  left: 0;
  z-index: 1;

  .${paperClasses.root} {
    background: ${({ theme }) => theme.palette.background.paper};
    transition: ${({ theme }) =>
      theme.transitions.create('all', {
        duration: theme.transitions.duration.shortest,
      })};
    padding: ${({ theme }) => theme.spacing()};
  }
`;

const SnippetWrapper = styled(Box)`
  display: flex;
  font-size: ${({ theme }) => theme.typography.body2.fontSize};
  line-height: 1.3;
  min-width: 100%;
  flex: 1;
  font-family: monospace;
  padding: ${({ theme }) => theme.spacing(1, 1, 1, 3)};

  .${paperClasses.root} {
    background: ${({ theme }) =>
      theme.palette.mode === 'dark'
        ? theme.palette.background.default
        : theme.palette.background.paper};
    flex: 1;
    width: 100%;
  }

  &.no-wrap {
    width: fit-content;
  }

  &.with-header {
    flex-direction: column;
  }

  &.wrap {
    max-width: 100%;

    ul,
    li {
      max-width: 100%;
    }

    span {
      white-space: break-spaces;
    }
  }
`;

const HeaderWrapper = styled(Box)`
  margin-bottom: ${({ theme }) => theme.spacing(1)};
`;

export type DataTreeLanguage = 'json' | 'xml';

type DataTreeContent = { data: object; language: 'json' } | { data: string; language: 'xml' };

export type DataTreeProps = {
  disableControls?: boolean;
  header?: ReactNode;
  initExpandAll?: boolean;
  initWordWrap?: boolean;
  maxHeight?: BoxProps['height'];
} & DataTreeContent;

export const DataTree: FC<DataTreeProps> = ({
  data,
  language,
  disableControls,
  header,
  initExpandAll,
  initWordWrap,
  maxHeight = 1,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const oppositeMode = useOppositeColorMode();

  const [expandAll, setExpandAll] = useState(initExpandAll || language === 'xml');
  const [wordWrap, setWordWrap] = useState(initWordWrap || language === 'json');

  const jsonTreeColorMap = {
    Boolean: theme.palette.success[oppositeMode],
    Date: theme.palette.error[oppositeMode],
    Function: theme.palette.error[oppositeMode],
    Null: theme.palette.text.disabled,
    Number: theme.palette.secondary[oppositeMode],
    String:
      theme.palette.mode === 'dark'
        ? theme.palette.warning[oppositeMode]
        : theme.palette.custom.content.page,
    Symbol: theme.palette.error[oppositeMode],
    Undefined: theme.palette.text.disabled,
  };

  return (
    <Fragment>
      <Container className={classNames({ wrap: wordWrap })} height={1} maxHeight={maxHeight}>
        {!disableControls && (
          <ControlWrapper
            className={classNames({
              [`margin--dense`]: language === 'xml',
            })}
          >
            <Paper square>
              <Stack direction='row' spacing={1}>
                <FormControlLabel
                  control={
                    <Switch checked={expandAll} onChange={(_, checked) => setExpandAll(checked)} />
                  }
                  label={t('expandAll')}
                />

                <FormControlLabel
                  control={
                    <Switch checked={wordWrap} onChange={(_, checked) => setWordWrap(checked)} />
                  }
                  label={t('wordWrap')}
                />
              </Stack>
            </Paper>
          </ControlWrapper>
        )}

        <SnippetWrapper
          className={classNames({
            wrap: wordWrap,
            'no-wrap': !wordWrap,
            'with-header': Boolean(header),
          })}
        >
          {header && <HeaderWrapper>{header}</HeaderWrapper>}

          {language === 'xml' && (
            <Box
              {...(wordWrap
                ? {}
                : {
                    sx: { div: { whiteSpace: 'pre' } },
                  })}
            >
              <XMLViewer
                collapsible
                indentSize={3}
                initialCollapsedDepth={expandAll ? undefined : 0}
                theme={{
                  attributeKeyColor: theme.palette.success[oppositeMode],
                  attributeValueColor: theme.palette.warning[oppositeMode],
                  separatorColor: theme.palette.text.disabled,
                  tagColor: theme.palette.info.main,
                  textColor: theme.palette.text.primary,
                }}
                xml={data}
              />
            </Box>
          )}

          {language === 'json' && (
            <JSONTree
              data={data}
              hideRoot
              key={String(expandAll)}
              shouldExpandNodeInitially={expandAll ? () => true : undefined}
              theme={{
                arrowSign: { color: theme.palette.custom.content.collection },
                label: { color: theme.palette.custom.content.collection },
                tree: { backgroundColor: 'transparent', display: 'flex' },
                nestedNodeItemString: ({ style }) => ({
                  style: { ...style, color: theme.palette.text.primary },
                }),
                nestedNodeItemType: { color: theme.palette.text.primary },
                valueLabel: {
                  color: theme.palette.custom.content.collection,
                },
                valueText: ({ style }, nodeType) => ({
                  style: {
                    ...style,
                    ...(wordWrap ? {} : { whiteSpace: 'nowrap' }),
                    color: jsonTreeColorMap[nodeType as keyof typeof jsonTreeColorMap],
                    paddingRight: 10,
                    wordBreak: 'break-word',
                  },
                }),
              }}
            />
          )}
        </SnippetWrapper>
      </Container>
    </Fragment>
  );
};
